import { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { signalRService } from '../services/signalr';
import { useToast } from '../hooks/useToast.tsx';
import { APP_VERSION } from '../version';
import { useTheme } from '../contexts/ThemeContext';
import { useRestaurantName } from '../hooks/useRestaurantName';
import { authApi, permissoesApi, versionApi } from '../services/api';
import {
  LayoutDashboard,
  Calendar,
  Users,
  Table,
  MapPin,
  Settings,
  History,
  UserCog,
  LogOut,
  Menu,
  X,
  Wifi,
  WifiOff,
  UtensilsCrossed,
  Moon,
  Sun,
  User,
  Shield,
  Radio,
  BarChart3,
} from 'lucide-react';

const navigation = [
  { name: 'Mapa', href: '/mapa', icon: LayoutDashboard, recurso: 'mapa' },
  { name: 'Reservas', href: '/reservas', icon: Calendar, recurso: 'reservas' },
  { name: 'Clientes', href: '/clientes', icon: Users, recurso: 'clientes' },
  { name: 'Mesas', href: '/mesas', icon: Table, recurso: 'mesas' },
  { name: 'Locais', href: '/locais', icon: MapPin, recurso: 'locais' },
  { name: 'Canais', href: '/canais', icon: Radio, recurso: 'canais' },
  { name: 'Histórico', href: '/historico', icon: History, recurso: 'historico' },
  { name: 'Estatísticas', href: '/estatisticas', icon: BarChart3, recurso: 'estatisticas' },
  { name: 'Configurações', href: '/configuracoes', icon: Settings, recurso: 'configuracoes' },
];

const adminNavigation = [
  { name: 'Utilizadores', href: '/utilizadores', icon: UserCog, recurso: 'utilizadores' },
  { name: 'Roles', href: '/roles', icon: Shield, recurso: 'roles' },
  { name: 'Base de Dados', href: '/admin-db', icon: Settings, recurso: 'admin_db' },
];

const mobilePrimary = ['/mapa', '/reservas', '/clientes', '/mesas', '/estatisticas'];

export function Layout() {
  const { user, logout, isAuthenticated } = useAuth();
  const { success, error, warning, info } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { theme, toggle } = useTheme();
  const restaurantName = useRestaurantName();
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'connecting' | 'disconnected'>('connecting');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [apiVersion, setApiVersion] = useState<string>(APP_VERSION);
  const [permsMap, setPermsMap] = useState<Map<string, boolean> | null>(null);

  useEffect(() => {
    if (!user) { setPermsMap(null); return; }
    const raw = String(user.role);
    const roleNum = !isNaN(Number(raw)) ? Number(raw) : raw === 'SuperAdmin' ? 0 : raw === 'Admin' ? 1 : 2;
    permissoesApi.getAll(roleNum)
      .then(r => {
        const arr = r.data ?? [];
        const m = new Map<string, boolean>();
        for (const p of arr) {
          const rec = String(p.recurso ?? '').toLowerCase();
          m.set(rec, !!p.podeAceder);
        }
        setPermsMap(m);
      })
      .catch(() => setPermsMap(new Map()))
  }, [user?.role, user?.id]);

  const podeAceder = (recurso: string) => {
    // Durante o carregamento, mostra itens para SuperAdmin/Admin (role 0/1) por defeito
    // Para outros roles, aguarda carregar
    if (!permsMap) {
      const raw = String(user?.role ?? '');
      const roleNum = !isNaN(Number(raw)) ? Number(raw) : raw === 'SuperAdmin' ? 0 : raw === 'Admin' ? 1 : 2;
      return roleNum <= 1; // SuperAdmin/Admin veem tudo enquanto carrega
    }
    return permsMap.get(recurso.toLowerCase()) ?? false;
  };

  const layoutHandlersRef = useRef<Parameters<typeof signalRService.start>[0] | null>(null);
  useEffect(() => {
    versionApi.get().then(d => { if (d?.data?.version) setApiVersion(d.data.version); }).catch(() => {});

    const setupSignalR = async () => {
      signalRService.setTokenRefreshCallback(async () => {
        try { await authApi.me(); } catch { /* sessão inválida: 401 já limpa AuthContext */ }
      });
      const handlers: Parameters<typeof signalRService.start>[0] = {
        onConnectionChange: (connected) => {
          setConnectionStatus(connected ? 'connected' : 'disconnected');
          if (!connected) warning('Ligação perdida. A tentar reconectar...');
          else success('Ligação restabelecida');
        },
        onReservationCreated: () => info('Nova reserva criada'),
        onReservationUpdated: () => info('Reserva atualizada'),
        onReservationCancelled: () => warning('Reserva cancelada'),
        onReservationConfirmed: () => success('Reserva confirmada'),
        onHistoryCreated: () => info('Novo registo no histórico'),
        onTableChanged: () => info('Mesa alterada'),
      };
      layoutHandlersRef.current = handlers;
      try {
        await signalRService.start(handlers);
        setConnectionStatus('connected');
      } catch {
        setConnectionStatus('disconnected');
        error('Não foi possível ligar ao servidor em tempo real');
      }
    };
    if (isAuthenticated) setupSignalR();
    return () => {
      const h = layoutHandlersRef.current;
      if (h) signalRService.stop(h);
      else signalRService.stop();
    };
  }, [isAuthenticated]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch {
      error('Erro ao fazer logout');
    }
  };

  const connectionIcon = connectionStatus === 'connected' ? (
    <Wifi className="w-5 h-5 text-green-600" aria-label="Ligado" />
  ) : connectionStatus === 'connecting' ? (
    <Wifi className="w-5 h-5 text-yellow-600 animate-pulse" aria-label="A ligar..." />
  ) : (
    <WifiOff className="w-5 h-5 text-red-600" aria-label="Desligado" />
  );

  const visibleNavigation = navigation.filter(n => podeAceder(n.recurso));
  const visibleAdminNavigation = adminNavigation.filter(n => podeAceder(n.recurso));

  const currentTitle =
    visibleNavigation.find(n => location.pathname.startsWith(n.href))?.name ||
    visibleAdminNavigation.find(n => location.pathname.startsWith(n.href))?.name ||
    (podeAceder('perfil') && location.pathname.startsWith('/perfil') ? 'Perfil' : undefined) ||
    'inReservas';

  useEffect(() => {
    document.title = `${currentTitle} - ${restaurantName} - InReservas`;
  }, [currentTitle, restaurantName]);

  const allNavItems = [...visibleNavigation, ...visibleAdminNavigation];

  return (
    <div className="layout">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`sidebar ${sidebarOpen ? 'open' : ''}`}
        role="navigation"
        aria-label="Navegação principal"
      >
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <div className="sidebar-logo" aria-hidden="true">
              <UtensilsCrossed className="w-5 h-5" />
            </div>
            <div>
              <h1 className="sidebar-title">{restaurantName}</h1>
              <span className="sidebar-subtitle">Gestão de reservas</span>
            </div>
          </div>
          <button
            className="sidebar-close"
            onClick={() => setSidebarOpen(false)}
            aria-label="Fechar menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="sidebar-nav" aria-label="Menu principal">
          <ul>
            {visibleNavigation.map((item) => (
              <li key={item.name}>
                <NavLink
                  to={item.href}
                  className={({ isActive }) =>
                    `nav-link ${isActive ? 'active' : ''}`
                  }
                  onClick={() => setSidebarOpen(false)}
                  aria-current={location.pathname.startsWith(item.href) ? 'page' : undefined}
                >
                  <item.icon className="w-5 h-5" aria-hidden="true" />
                  <span>{item.name}</span>
                </NavLink>
              </li>
            ))}

            {visibleAdminNavigation.length > 0 && (
              <>
                <li className="sidebar-nav-label" aria-hidden="true">Administração</li>
                {visibleAdminNavigation.map((item) => (
                  <li key={item.name}>
                    <NavLink
                      to={item.href}
                      className={({ isActive }) =>
                        `nav-link ${isActive ? 'active' : ''}`
                      }
                      onClick={() => setSidebarOpen(false)}
                      aria-current={location.pathname.startsWith(item.href) ? 'page' : undefined}
                    >
                      <item.icon className="w-5 h-5" aria-hidden="true" />
                      <span>{item.name}</span>
                    </NavLink>
                  </li>
                ))}
              </>
            )}
          </ul>
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar" aria-hidden="true">
              {(user?.nome?.charAt(0) || '?').toUpperCase()}
            </div>
            <div className="user-details">
              <span className="user-name">{user?.nome}</span>
              <span className="user-role">{user?.role}</span>
            </div>
          </div>
          <button
            className="btn btn-ghost btn-sm w-full justify-start"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4" aria-hidden="true" />
            <span>Sair</span>
          </button>
          <div className="sidebar-version" aria-label={`Versão ${APP_VERSION}`}>
            <span title={`UI ${APP_VERSION}`}>UI v{APP_VERSION}</span>
            <span className="version-dot">•</span>
            <span title={`API ${apiVersion}`}>API v{apiVersion}</span>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="main-wrapper">
        {/* Header */}
        <header className="header" role="banner">
          <button
            className="header-menu-btn"
            onClick={() => setSidebarOpen(true)}
            aria-label="Abrir menu"
            aria-expanded={sidebarOpen}
          >
            <Menu className="w-6 h-6" aria-hidden="true" />
          </button>

          <div className="header-title">
            <h1>{currentTitle}</h1>
          </div>

          <div className="header-actions">
            <div className="connection-status" role="status" aria-live="polite">
              {connectionIcon}
              <span className="sr-only">
                {connectionStatus === 'connected' ? 'Ligado' : connectionStatus === 'connecting' ? 'A ligar' : 'Desligado'}
              </span>
            </div>

            <div className="user-menu-wrapper">
              <button
                className="header-user-btn"
                onClick={() => setShowUserMenu(v => !v)}
                onBlur={(e) => { if (!e.currentTarget.parentElement?.contains(e.relatedTarget as Node)) setShowUserMenu(false); }}
                aria-expanded={showUserMenu}
                aria-haspopup="true"
                aria-label="Menu do utilizador"
              >
                <div className="user-avatar-sm" aria-hidden="true">
                  {(user?.nome?.charAt(0) || '?').toUpperCase()}
                </div>
              </button>

              {showUserMenu && (
                <div className="user-dropdown" role="menu">
                  <div className="dropdown-header">
                    <span className="dropdown-name">{user?.nome}</span>
                    <span className="dropdown-role">{user?.role}</span>
                  </div>
                  <button
                    className="dropdown-item"
                    role="menuitem"
                    onClick={() => { navigate('/perfil'); setShowUserMenu(false); }}
                  >
                    <User className="w-4 h-4" />
                    <span>Meu perfil</span>
                  </button>
                  <button
                    className="dropdown-item"
                    role="menuitem"
                    onClick={() => { toggle(); setShowUserMenu(false); }}
                  >
                    {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                    <span>{theme === 'dark' ? 'Modo claro' : 'Modo escuro'}</span>
                  </button>
                  <button
                    className="dropdown-item"
                    role="menuitem"
                    onClick={handleLogout}
                  >
                    <LogOut className="w-4 h-4" aria-hidden="true" />
                    <span>Sair</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main id="main-content" className="main-content" role="main" tabIndex={-1}>
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom navigation */}
      <nav className="mobile-nav" aria-label="Navegação móvel">
        <ul className="mobile-nav-list">
          {allNavItems
            .filter(item => mobilePrimary.includes(item.href))
            .map(item => (
              <li key={item.href} className="mobile-nav-item-wrap">
                <NavLink
                  to={item.href}
                  className={({ isActive }) =>
                    `mobile-nav-item ${isActive ? 'active' : ''}`
                  }
                  aria-label={item.name}
                >
                  <item.icon aria-hidden="true" />
                  <span>{item.name}</span>
                </NavLink>
              </li>
            ))}
        </ul>
      </nav>

      <ToastContainer />
    </div>
  );
}

function ToastContainer() {
  const { toasts, removeToast } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div className="toast-container" aria-live="polite" aria-label="Notificações">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`toast toast-${t.type}`}
          onClick={() => removeToast(t.id)}
          role="alert"
        >
          <span className="toast-message">{t.message}</span>
          <button className="toast-close" onClick={(e) => { e.stopPropagation(); removeToast(t.id); }} aria-label="Fechar">
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
}